/* eslint-disable react-hooks/exhaustive-deps */
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import ListIcon from "@mui/icons-material/List";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import TuneIcon from "@mui/icons-material/Tune";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import { Box, Button, Paper, Typography } from "@mui/material";
import ContentShell from "components/ContentShell";
import CustomButton from "components/CustomButton";
import { SimpleSelectInput } from "components/FormSelectInput";
import { SimpleTextInput } from "components/FormTextInput";
import { useLoadingOverlay } from "components/LoadingOverlay";
import ModalContainer from "components/ModalContainer";
import ModalLayout from "components/ModalLayout";
import SearchableInput from "components/SearchableInput";
import Table from "components/Table";
import TranslationButtons from "components/TranslationButtons";
import FilePicker from "components/FilePicker";
import configData from "config/config.json";
import useOpenClose from "hooks/useOpenClose";
import { useSnackbar } from "hooks/useSnackbar";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ViewLanguageModal from "screens/modals/ViewLanguageModal";
import {
  createLeagueAndMileStone,
  leagueInfo,
  updateLeagueAndMileStone,
} from "services/leagueServices";
import {
  packInfo as getPackInfo,
  getPlayerPacks as getRewardPacks,
} from "services/packServices";
import { uploadImageV2 } from "services/uploadService";
import useStyles from "styles/league/leagueEditor";
import {
  DEFAULT_IMAGE_DIMENSIONS,
  ImageType,
} from "utils/imageConstants";
import { leagueSchema } from "utils/validation";

const milestoneTemplate = {
  descText: "",
  descTextTrans: {},
  number: 1,
  steps: 1,
  rewardPack: null,
  rewardCoins: 0,
  rewardPackCount: 0
};

const initialValue = {
  name: "",
  number: 1,
  k1: 1,
  k2: 1,
  floorValue: null,
  ceilValue: null,
  milestones: [milestoneTemplate],
  kWeights: {} as Record<string, number>,
  kWeightsFe: [] as Array<[number, number]>,
};

const defaultPageSize = 20,
  defaultPageNo = 1;

const SectionCard = ({
  step,
  icon,
  title,
  subtitle,
  accent = "#1976d2",
  children,
  headerExtra,
}: {
  step: number;
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  accent?: string;
  children: React.ReactNode;
  headerExtra?: React.ReactNode;
}) => (
  <Paper
    elevation={0}
    sx={{
      border: "1px solid #e0e0e0",
      borderRadius: 3,
      mb: 3,
      overflow: "hidden",
      backgroundColor: "#ffffff",
      transition: "box-shadow 0.3s",
      "&:hover": { boxShadow: `0 2px 16px ${accent}14` },
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2.5,
        py: 2,
        borderBottom: "1px solid #eeeeee",
        borderLeft: `4px solid ${accent}`,
        background: `linear-gradient(135deg, ${accent}0d 0%, transparent 100%)`,
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          background: `linear-gradient(135deg, ${accent}, ${accent}cc)`,
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 13,
          fontWeight: 700,
          flexShrink: 0,
          boxShadow: `0 2px 8px ${accent}40`,
        }}
      >
        {step}
      </Box>
      <Box sx={{ color: accent, display: "flex", fontSize: 20 }}>{icon}</Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
      {headerExtra}
    </Box>
    <Box sx={{ p: 3 }}>{children}</Box>
  </Paper>
);

interface MilestoneEditorProps {
  id: string;
  index: number;
  milestone: typeof milestoneTemplate & { rewardPackImageUrl?: string };
  rewardOptions: { id: string; label: string }[];
  onSearch: (searchValue: string) => void;
  onMilestoneChange: (e: any, index: number) => void;
  handleOpenModal: (type: string, field: string, milestone: number) => void;
  onImageSelect: (index: number, file: File | null, dimensions?: any) => void;
  selectedImage: File | null;
}

const MileStoneEditor = ({
  id,
  index,
  onMilestoneChange,
  milestone,
  rewardOptions,
  onSearch,
  handleOpenModal,
  onImageSelect,
  selectedImage,
}: MilestoneEditorProps) => {
  const styles = useStyles();
  const selected = rewardOptions.find(
    (rewardOp) => rewardOp.id === milestone.rewardPack
  );

  const onChange = (e: any) => onMilestoneChange(e, index);

  return (
    <Box className={styles.milestoneCard}>
      <Box className={styles.milestoneCardHeader}>
        <Box className={styles.milestoneTitle}>
          <Box className={styles.milestoneBadge}>{index + 1}</Box>
          <Typography className={styles.milestoneTitleText}>
            Milestone {index + 1}
          </Typography>
        </Box>
        {index !== 0 && (
          <DeleteIcon
            className={styles.milestoneDeleteIcon}
            onClick={() => onChange({ target: { name: "delete" } })}
          />
        )}
      </Box>

      <Box className={styles.milestoneFieldsGrid}>
        <Box className={styles.milestoneRewardField}>
          <SearchableInput
            selected={id !== "create" ? selected : milestone.rewardPack}
            name="rewardPack"
            options={rewardOptions}
            label="Reward Packs"
            placeholder="search packs"
            onInputChange={onSearch}
            onOptionSelect={(e) => onMilestoneChange(e, index)}
            customStyle={{ width: "100%" }}
          />
        </Box>
        <Box className={styles.smallField}>
          <SimpleTextInput
            type="number"
            label="Number *"
            name="number"
            value={milestone?.number}
            onChange={onChange}
          />
        </Box>
        <Box className={styles.smallField}>
          <SimpleTextInput
            type="number"
            label="Steps *"
            name="steps"
            value={milestone?.steps}
            onChange={onChange}
          />
        </Box>
        <Box className={styles.smallField}>
          <SimpleTextInput
            type="number"
            label="Reward Coins"
            name="rewardCoins"
            value={milestone?.rewardCoins}
            onChange={onChange}
          />
        </Box>
        <Box className={styles.smallField}>
          <SimpleTextInput
            type="number"
            label="Pack count *"
            name="rewardPackCount"
            value={milestone?.rewardPackCount}
            onChange={onChange}
          />
        </Box>
        <Box className={styles.milestoneDescField}>
          <SimpleTextInput
            label="Description"
            name="descText"
            value={milestone?.descText}
            onChange={onChange}
            button={
              <TranslationButtons
                field={"descTextTrans"}
                onClick={(...props) => handleOpenModal(...props, index)}
              />
            }
          />
        </Box>
        <Box className={styles.milestoneImageField}>
          <Typography className={styles.milestoneImageCaption}>
            Display Image
          </Typography>
          <FilePicker
            name={`milestone-image-${index}`}
            onFileSelect={(file, dimensions) =>
              onImageSelect(index, file, dimensions)
            }
            imageType={ImageType.MILESTONE_REWARD_IMAGE}
            enableDimensionControl={true}
          />
          {milestone.rewardPackImageUrl && !selectedImage && (
            <img
              src={milestone.rewardPackImageUrl}
              alt="Current reward"
              className={styles.imagePreview}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

const LeagueEditor = () => {
  const styles = useStyles();
  const location = useLocation();
  const { showSnackbar } = useSnackbar();
  const id = location.pathname.split("/").slice(-1)[0];
  const navigate = useNavigate();
  const { showLoadingOverlay, hideLoadingOverlay } = useLoadingOverlay();
  const [leagueDetails, setLeagueDetails] = useState(initialValue);
  const [milestoneImages, setMilestoneImages] = useState<(File | null)[]>([null]);
  const [milestoneImageDimensions, setMilestoneImageDimensions] = useState<(any | null)[]>([null]);
  const [loading, setLoading] = useState(false);
  const [handleEvents, setHandleEvents] = useState({} as any);
  const [rewards, setRewards] = useState([]);
  const [options, setOptions] = useState({
    leagueOptions: Array.from({ length: 10 }, (v, k) => ({
      id: `${k + 1}`,
      label: `${k + 1}`,
    })),
    rewardOptions: [],
  });
  // eslint-disable-next-line no-unused-vars
  const [searchPagination, setSearchPagination] = useState({
    pageNo: defaultPageNo,
    pageSize: defaultPageSize,
    hasMore: false,
  });

  const [modalData, setModalData] = useState({
    language: "",
    inputValue: "",
    field: null,
    viewData: [],
  });

  const { isOpen, extraData, open, close, updateExtra } = useOpenClose<{
    data?: { kValue?: string; kWeight?: string };
    index?: number;
    modalContentType?: string;
  }>();
  const {
    isOpen: isOpenLangModal,
    extraData: langExtraData,
    open: openLangModel,
    close: closeLangModal,
    updateExtra: updateLangExtraData,
  } = useOpenClose<{ milestone?: any }>();

  const generateOptions = (items: any) =>
    items.map((item, index) => ({
      id: item?._id,
      label: item?.idFromExcel
        ? `${item?.name}(${item?.idFromExcel})`
        : item?.name,
    }));

  const convertToPayload = (league: any) => {
    // modify payload here
    return {
      ...league,
      milestones:
        league?.milestones?.map((stone) => {
          return {
            ...stone,
            rewardPack: stone.rewardPack?.id || stone.rewardPack,
          };
        }) || [],
    };
  };

  const onMilestoneImageSelect = (index: number, file: File | null, dimensions?: any) => {
    setMilestoneImages((prev) => {
      const updated = [...prev];
      updated[index] = file;
      return updated;
    });
    setMilestoneImageDimensions((prev) => {
      const updated = [...prev];
      updated[index] = dimensions ?? null;
      return updated;
    });
  };

  const uploadMilestoneImage = async (uploadUrl: string, image: File, dimensions?: any) => {
    const defaultDims = DEFAULT_IMAGE_DIMENSIONS[ImageType.MILESTONE_REWARD_IMAGE];
    await uploadImageV2({
      data: image,
      uploadUrl,
      resize: true,
      resizeToWidth: dimensions?.targetWidth ?? defaultDims.width,
      resizeToHeight: dimensions?.targetHeight ?? defaultDims.height,
    });
  };

  const submit = async () => {
    showLoadingOverlay();
    const payload = convertToPayload(leagueDetails);

    milestoneImages.forEach((img, index) => {
      if (img && payload.milestones[index]) {
        payload.milestones[index].fileType = "image/webp";
      }
    });

    try {
      let resp;

      await leagueSchema.validate(leagueDetails);

      const tempLeagueDetails = JSON.parse(JSON.stringify(leagueDetails));

      for (
        let index = 0;
        index < tempLeagueDetails.milestones.length;
        index++
      ) {
        const milestone = tempLeagueDetails.milestones[index];

        if (milestone.rewardPack) {
          tempLeagueDetails.milestones[index].rewardPack =
            tempLeagueDetails.milestones[index].rewardPack.id;
        }
      }

      if (id === "create") {
        resp = await createLeagueAndMileStone(payload);
      } else {
        resp = await updateLeagueAndMileStone(payload, id);
      }

      const responseMilestones: any[] = resp?.data?.milestones ?? [];
      try {
        await Promise.all(
          responseMilestones.map((ms, index) => {
            if (ms.uploadRewardImageUrl && milestoneImages[index]) {
              return uploadMilestoneImage(
                ms.uploadRewardImageUrl,
                milestoneImages[index],
                milestoneImageDimensions[index]
              );
            }
          })
        );
        showSnackbar(resp.message, "success");
      } catch (uploadErr) {
        showSnackbar("League saved but image upload failed. Please re-save.", "warning");
      }
      hideLoadingOverlay();
      navigate(configData.routes.leagues.leagues);
    } catch (error) {
      console.log(error);

      if (error?.inner) {
        error.inner.forEach((error) => {
          showSnackbar(error.message, "error");
        });
      }

      hideLoadingOverlay();
      showSnackbar(error?.message);
    }
  };

  const cancel = () => navigate(configData.routes.leagues.leagues);

  const handleNumberFieldChange = ({ fieldName, value, _data }, flag) => {
    let data = { ..._data };
    const _value = parseInt(value) ?? null; //Number(value?.split(".")[0]) ?? "";
    let updatedData = {
      ...data,
      [fieldName]: _value,
    };

    if (flag) updatedData["name"] = `League_${value}`;

    return updatedData;
  };

  const onChange = (e, leagueFlag) => {
    const { name, value } = e?.target;
    let data = { ...leagueDetails };

    const eventHandler = handleEvents[name];

    if (eventHandler) {
      const resp = eventHandler(
        { fieldName: name, value, _data: data },
        leagueFlag
      );

      data = resp;
    } else {
      data = {
        ...data,
        [name]: value,
      };
    }

    setLeagueDetails(data);
  };

  const onMilestoneChange = (e, index) => {
    const { name, value } = e?.target;

    let milestones = [...leagueDetails.milestones];

    const eventHandler = handleEvents[name];

    if (name === "delete") {
      milestones = milestones.filter((_, idx) => idx !== index);
      setMilestoneImages((prev) => prev.filter((_, idx) => idx !== index));
      setMilestoneImageDimensions((prev) => prev.filter((_, idx) => idx !== index));
    } else if (eventHandler) {
      milestones = [
        ...leagueDetails.milestones?.slice(0, index),
        {
          ...eventHandler({
            fieldName: name,
            value,
            _data: leagueDetails.milestones[index],
          }),
        },
        ...leagueDetails.milestones?.slice(index + 1),
      ];
    } else {
      milestones = [
        ...leagueDetails.milestones?.slice(0, index),
        {
          ...leagueDetails.milestones[index],
          [name]: value,
        },
        ...leagueDetails.milestones?.slice(index + 1),
      ];
    }

    if (name === "descText") {
      milestones[index][`${name}Trans`] ??= {};
      value
        ? (milestones[index][`${name}Trans`]["en"] = value)
        : delete milestones[index][`${name}Trans`]["en"];
    }

    setLeagueDetails((_state) => ({ ..._state, milestones }));
    setOptions((st) => ({ ...st, rewardOptions: generateOptions(rewards) }));
  };

  const add = () => {
    setLeagueDetails((_state) => {
      let milestones = [..._state.milestones, milestoneTemplate];
      return { ..._state, milestones };
    });
    setMilestoneImages((prev) => [...prev, null]);
    setMilestoneImageDimensions((prev) => [...prev, null]);
  };

  const removeAll = () => {
    setLeagueDetails((_state) => ({
      ..._state,
      milestones: [milestoneTemplate],
    }));
    setMilestoneImages([null]);
    setMilestoneImageDimensions([null]);
  };

  const fetchLeagueInfo = async () => {
    setLoading(true);
    try {
      const resp = await leagueInfo(id);
      setLeagueDetails(resp?.data);
      const milestoneCount = resp?.data?.milestones?.length ?? 0;
      setMilestoneImages(new Array(milestoneCount).fill(null));
      setMilestoneImageDimensions(new Array(milestoneCount).fill(null));

      const rewardPacks = new Set(
        resp.data.milestones.map((el) => el.rewardPack)
      );

      let _requests = [];
      for (let reward of Array.from(rewardPacks)) {
        if (!options.rewardOptions.find((el) => el.reward == reward))
          _requests.push(getPackInfo(reward));
      }

      const packInfo = await Promise.all(_requests);
      setOptions((st) => ({
        ...st,
        rewardOptions: [
          st.rewardOptions,
          ...packInfo.map((el) => ({
            id: el?.data?._id,
            label: el?.data?.name,
          })),
        ],
      }));
      setLoading(false);
    } catch (error) {
      setLoading(false);
      showSnackbar(error.message);
    }
  };

  const fetchRewardPacks = async (filters, init?) => {
    try {
      const resp = await getRewardPacks(filters);
      init && setRewards(resp?.items);
      setOptions((st) => ({
        ...st,
        rewardOptions: generateOptions(resp.items),
      }));
    } catch (error) {
      showSnackbar(error?.message || "Failed to fetch reward packs");
    }
  };

  const onSearchChange = async (searchValue) => {
    if (searchValue) {
      fetchRewardPacks({
        search: searchValue,
        skipPagination: true,
        ...searchPagination,
      });
    } else
      setOptions((st) => ({ ...st, rewardOptions: generateOptions(rewards) }));
  };

  const handleViewWeightage = () => {
    open();
    updateExtra({ data: null, modalContentType: "viewWeightage" });
  };

  const handleEditWeightage = ({ rowId, data, actionHandlers }: any) => {
    updateExtra({
      data: data,
      index: rowId,
      modalContentType: "editWeightage",
    });
  };

  const onWeightageChange = (e: any) => {
    const { name, value } = e?.target;
    updateExtra({ ...extraData, data: { ...extraData.data, [name]: value } });
  };

  const onWeightageSubmit = () => {
    close();
    setLeagueDetails((prevState) => {
      return {
        ...prevState,
        kWeights: {
          ...prevState.kWeights,
          [extraData.data.kValue]: parseInt(extraData.data.kWeight),
        },
        kWeightsFe: [
          ...prevState.kWeightsFe.slice(0, extraData.index),
          [parseInt(extraData.data.kValue), parseInt(extraData.data.kWeight)],
          ...prevState.kWeightsFe.slice(extraData.index + 1),
        ],
      };
    });
    open();
    updateExtra({ data: null, index: -1, modalContentType: "viewWeightage" });
  };

  const handleOpenModal = (type, field, milestone) => {
    updateLangExtraData({ milestone: milestone });
    setModalData((state) => {
      if (type === "view") {
        let data = getTranslatedData(field, milestone);
        return { ...state, viewData: data, field, modal: type };
      } else {
        return {
          ...state,
          field,
          modal: type,
          language: "",
          inputValue: "",
        };
      }
    });

    openLangModel();
  };

  const handleChange = (event: any) => {
    const { name, value } = event.target;
    setModalData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLangEditClick = (data: any) => {
    closeLangModal();
    setModalData((state) => ({
      ...state,
      language: data?.lang,
      inputValue: data?.inputValue,
      modal: "add",
    }));
    openLangModel();
  };

  const handleLangDeleteClick = (data: any) => {
    let milestone = langExtraData?.milestone;

    if ([undefined, null].includes(milestone)) {
      showSnackbar("Invalid milestone selected", "error");
      return;
    }

    if (data.lang === "en") {
      showSnackbar(
        "Cannot delete English translation, as it's a default languange",
        "error"
      );
      return;
    }

    // remove from view data
    setModalData((state) => ({
      ...state,
      viewData: state.viewData.filter((item) => item.lang !== data.lang),
    }));

    setLeagueDetails((prev) => {
      prev.milestones[milestone][modalData.field] = Object.keys(
        prev.milestones[milestone][modalData.field]
      ).reduce((acc, key) => {
        if (key !== data.lang) {
          acc[key] = prev.milestones[milestone][modalData.field][key];
        }
        return acc;
      }, {});
      return { ...prev };
    });
  };

  const handleAddTranslation = () => {
    let milestone = langExtraData?.milestone;

    if ([undefined, null].includes(milestone)) {
      showSnackbar("Invalid milestone selected", "error");
      return;
    }

    const { field } = modalData;
    if (!field) {
      showSnackbar("Confused which field to add the translation", "error");
      return;
    }

    setLeagueDetails((prev) => {
      const updatedMilestones = [...prev.milestones];
      const updatedField = { ...updatedMilestones[milestone][field] };

      if (modalData.inputValue) {
        updatedField[modalData.language] = modalData.inputValue;
      } else {
        delete updatedField[modalData.language];
      }

      updatedMilestones[milestone] = {
        ...updatedMilestones[milestone],
        [field]: updatedField,
      };

      return { ...prev, milestones: updatedMilestones };
    });

    // if english translation is available set it field
    if (modalData.language === "en") {
      setLeagueDetails((prev) => {
        const updatedMilestones = [...prev.milestones];
        updatedMilestones[milestone] = {
          ...updatedMilestones[milestone],
          descText: modalData.inputValue,
        };

        return { ...prev, milestones: updatedMilestones };
      });
    }
    setModalData((prev) => ({
      ...prev,
      inputValue: "",
      language: "",
      field: null,
    }));
    closeLangModal();
  };

  const getTranslatedData = (field, milestone) => {
    const objEntries = Object.entries(
      leagueDetails.milestones[milestone][field] ?? {}
    );

    if (!objEntries?.length) return [];
    const _translatedData = objEntries?.map(([key, value]) => ({
      lang: key,
      inputValue: value,
    }));
    if (!_translatedData) return [];
    return _translatedData;
  };

  useEffect(() => {
    fetchRewardPacks(searchPagination, true);
    if (id !== "create") fetchLeagueInfo();
    const eventsMaps = {
      k1: handleNumberFieldChange,
      k2: handleNumberFieldChange,
      steps: handleNumberFieldChange,
      number: handleNumberFieldChange,
      floorValue: handleNumberFieldChange,
      ceilValue: handleNumberFieldChange,
      rewardCoins: handleNumberFieldChange,
      rewardPackCount: handleNumberFieldChange,
    };

    setHandleEvents(eventsMaps);
  }, []);

  const milestoneCount = leagueDetails?.milestones?.length ?? 0;

  return (
    <ContentShell
      showBackButton
      onBackButtonClick={() => navigate(-1)}
      title={`${id === "create" ? "Create" : "Edit"} League & Milestones`}
      loading={loading}
      actions={
        <Box className={styles.buttonContainer}>
          <Button
            type="button"
            variant="outlined"
            onClick={cancel}
            className={styles.cancelButton}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            onClick={submit}
            className={styles.saveButton}
          >
            {id === "create" ? "Create League" : "Save Changes"}
          </Button>
        </Box>
      }
    >
      <Box className={styles.mainContainer}>
        {/* ── 1. League Configuration ─────────────────────────── */}
        <SectionCard
          step={1}
          icon={<TuneIcon fontSize="small" />}
          title="League Configuration"
          subtitle="Basic info, K-factors and value bounds"
          accent="#1976d2"
        >
          <Box className={styles.formGrid}>
            <Box className={styles.field}>
              <SimpleSelectInput
                name="number"
                label="League Number"
                value={leagueDetails.number}
                options={options.leagueOptions}
                onChange={(e) => onChange(e, true)}
              />
            </Box>
            <Box className={styles.field}>
              <SimpleTextInput
                type="text"
                label="League Name"
                name="name"
                value={leagueDetails.name}
                onChange={onChange}
              />
            </Box>
            <Box className={styles.field}>
              <SimpleTextInput
                type="number"
                label="K1 *"
                name="k1"
                value={leagueDetails.k1}
                onChange={onChange}
              />
            </Box>
            <Box className={styles.field}>
              <SimpleTextInput
                type="number"
                label="K2 *"
                name="k2"
                value={leagueDetails.k2}
                onChange={onChange}
              />
            </Box>
            <Box className={styles.field}>
              <SimpleTextInput
                type="number"
                label="Floor Value"
                name="floorValue"
                value={leagueDetails.floorValue}
                onChange={onChange}
              />
            </Box>
            <Box className={styles.field}>
              <SimpleTextInput
                type="number"
                label="Ceil Value"
                name="ceilValue"
                value={leagueDetails.ceilValue}
                onChange={onChange}
              />
            </Box>
          </Box>
          <Box className={styles.weightageRow}>
            <CustomButton
              startIcon={<ListIcon />}
              onClick={handleViewWeightage}
              className={styles.weightageButton}
            >
              View Weightage
            </CustomButton>
          </Box>
        </SectionCard>

        {/* ── 2. Milestones ────────────────────────────────────── */}
        <SectionCard
          step={2}
          icon={<EmojiEventsIcon fontSize="small" />}
          title="Milestones"
          subtitle="Define progression goals and rewards for this league"
          accent="#6a1b9a"
          headerExtra={
            <Box className={styles.milestoneCount}>
              {milestoneCount} {milestoneCount === 1 ? "milestone" : "milestones"}
            </Box>
          }
        >
          <Box className={styles.milestoneToolbar}>
            <Typography variant="body2" color="text.secondary">
              Add milestones progressively. The first milestone cannot be
              removed.
            </Typography>
            <Box className={styles.milestoneToolbarButtons}>
              <Button
                size="small"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={add}
                className={styles.addMilestoneButton}
              >
                Add Milestone
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<DeleteForeverIcon />}
                onClick={removeAll}
                className={styles.removeAllMilestoneButton}
              >
                Remove All
              </Button>
            </Box>
          </Box>

          <Box className={styles.milestoneList}>
            {milestoneCount === 0 ? (
              <Box className={styles.emptyMilestones}>
                <FlagOutlinedIcon sx={{ fontSize: 32, color: "#bdbdbd" }} />
                <Typography variant="body2" sx={{ mt: 1 }}>
                  No milestones yet. Click <b>Add Milestone</b> to create one.
                </Typography>
              </Box>
            ) : (
              leagueDetails?.milestones?.map((milestone, k) => (
                <MileStoneEditor
                  key={k}
                  id={id}
                  index={k}
                  milestone={milestone}
                  onSearch={onSearchChange}
                  rewardOptions={options.rewardOptions}
                  onMilestoneChange={onMilestoneChange}
                  handleOpenModal={handleOpenModal}
                  onImageSelect={onMilestoneImageSelect}
                  selectedImage={milestoneImages[k] ?? null}
                />
              ))
            )}
          </Box>
        </SectionCard>
      </Box>
      <ModalContainer openModel={isOpen} handleClose={close}>
        <ModalLayout isLargeContainer={false} hasPadding={true}>
          <Box className={styles.viewDataContainer}>
            <Typography className={styles.weightageModalTitle}>
              {(extraData.modalContentType === "editWeightage" ? "Edit " : "") +
                "League Weightages"}
            </Typography>
            {extraData.modalContentType === "viewWeightage" ? (
              <Table
                data={leagueDetails?.kWeightsFe?.map((el) => ({
                  kValue: el[0],
                  kWeight: el[1],
                }))}
                hidePagination={true}
                actions={(data) => (
                  <CustomButton onClick={() => handleEditWeightage(data)}>
                    Edit
                  </CustomButton>
                )}
                columns={[
                  {
                    id: "kValue",
                    label: "K Value",
                    alignHead: "center",
                    alignBody: "center",
                    isSortable: false,
                  },
                  {
                    id: "kWeight",
                    label: "K Weight",
                    alignHead: "center",
                    alignBody: "center",
                    isSortable: false,
                  },
                  {
                    id: "actions",
                    label: "Actions",
                  },
                ] as any}
              />
            ) : (
              <Box style={{ display: "flex", flexFlow: "column", gap: 10 }}>
                <Box style={{ display: "flex", gap: 5 }}>
                  <Box className={styles.field}>
                    <SimpleTextInput
                      type="number"
                      label="K-Value"
                      name="kValue"
                      value={extraData?.data?.kValue}
                      onChange={onWeightageChange}
                    />
                  </Box>
                  <Box className={styles.field}>
                    <SimpleTextInput
                      type="number"
                      label="K-Weight"
                      name="kWeight"
                      value={extraData?.data?.kWeight}
                      onChange={onWeightageChange}
                    />
                  </Box>
                </Box>
                <Button
                  type="submit"
                  variant="contained"
                  onClick={onWeightageSubmit}
                >
                  Save
                </Button>
              </Box>
            )}
          </Box>
        </ModalLayout>
      </ModalContainer>

      <ViewLanguageModal
        isMultiline
        label="Select Language"
        isOpen={isOpenLangModal}
        close={closeLangModal}
        onChange={handleChange}
        handleOnSubmit={handleAddTranslation}
        actionHandlers={{
          handleLangEditClick: handleLangEditClick,
          handleLangDeleteClick: handleLangDeleteClick,
        }}
        modalData={modalData}
        tableColumns={[
          {
            id: "lang",
            label: "Language",
            alignHead: "center",
            alignBody: "center",
          },
          {
            id: "inputValue",
            label: "Translation",
            alignHead: "center",
            alignBody: "center",
          },
          {
            id: "actions",
            label: "Actions",
            alignHead: "center",
            alignBody: "center",
          },
        ] as any}
      />
    </ContentShell>
  );
};

export default LeagueEditor;
